import type { HandbookChapter } from '../model';

/** Kanäle, Automatisierung, Benutzer, Schnittstellen -- was den Betrieb am Laufen hält. */

const mail: HandbookChapter = {
  id: 'mail',
  title: 'E-Mail',
  summary:
    'Postfächer, Anfragen ohne Kunden, Filterregeln, Signaturen, Mailvorlagen, Versandwarteschlange.',
  audience: 'admin',
  route: '/app/administration/mail',
  sections: [
    {
      id: 'postfach',
      title: 'Ein Postfach einrichten',
      blocks: [
        { kind: 'shot', file: 'mail', caption: 'Die E-Mail-Einrichtung mit ihren Reitern.' },
        {
          kind: 'steps',
          steps: [
            { text: '„Postfach hinzufügen“ klicken.' },
            { text: 'Server, Port, Benutzername und Kennwort eintragen, Ordner wählen.' },
            {
              text: '„Verschlüsselte Verbindung“ ankreuzen.',
              detail: 'Ohne sie liegt das Postfachkennwort im Klartext auf der Leitung.',
            },
            {
              text: 'Warteschlange wählen.',
              detail: 'Hier entstehen die Tickets aus diesem Postfach.',
            },
            {
              text: 'Entscheiden, ob Nachrichten nach dem Abruf im Postfach bleiben.',
              detail:
                'Zum Einrichten hilfreich, im Betrieb selten sinnvoll — das Postfach läuft sonst voll.',
            },
            {
              text: '„Jetzt abrufen“ zum Prüfen.',
              detail:
                'Der Hintergrunddienst holt jede Minute ab. Nach dem Einrichten will man es sofort wissen.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Das Kennwort erscheint nie wieder',
          text: 'Es wird verschlüsselt abgelegt und nur an den Mailserver gereicht. Ein Hash ließe sich nicht zurückrechnen — deshalb keine Anzeige und kein Export. Wer es vergisst, trägt ein neues ein.',
        },
      ],
    },
    {
      id: 'anfragen',
      title: 'Anfragen — Mails ohne Kunden',
      blocks: [
        {
          kind: 'shot',
          file: 'anfragen',
          caption: 'Die Anfragen mit ihren drei Listen: offen, zugewiesen, abgelehnt.',
        },
        {
          kind: 'text',
          text: 'Aus einer Mail wird nur dann ein Vorgang, wenn das System weiß, für welchen Kunden sie läuft. Es erkennt das an der Absenderadresse: Steht sie bei einem Ansprechpartner, ist der Kunde klar. Steht sie nirgends, zählt die Domäne hinter dem @ — ist sie beim Kunden hinterlegt, entsteht der Vorgang trotzdem. Trifft weder das eine noch das andere zu und betreuen Sie mehr als einen Kunden, wird geraten — und genau das tut das System nicht. Die Mail wartet stattdessen unter „Anfragen“.',
        },
        {
          kind: 'text',
          text: 'Die Anfragen stehen in der Navigation unter „Tickets“, für alle, die Anfragen sichten dürfen — auch ohne Zugriff auf die Einstellungen. Warten welche, steht ihre Zahl neben dem Eintrag.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wird diese Liste zu lang, fehlen Domänen',
          text: 'Landen immer wieder Mails desselben Kunden hier, ist das kein Fall für tägliche Handarbeit: Tragen Sie die E-Mail-Domäne dieses Kunden im Kundendialog ein. Von da an entsteht der Vorgang von selbst, und der Absender wird als Ansprechpartner übernommen. Das Kapitel „Kunden und Ansprechpartner“ beschreibt es.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Diese Liste braucht einen täglichen Blick',
          text: 'Wer hier schreibt, glaubt, seine Anfrage sei angekommen — eine Eingangsbestätigung geht in diesem Fall nicht hinaus. Eine Woche ohne Durchsicht heißt eine Woche Schweigen gegenüber jemandem, der wartet. Deshalb steht der Weg dorthin in der Ticketliste und nicht in der Verwaltung: oben rechts neben „Kanban-Board“ und „Neues Ticket“. Wartet etwas, trägt der Verweis eine Blase mit der Zahl; ist nichts da, steht dort keine — dann müssen Sie auch nicht hinsehen.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Absender, Betreff und Auszug lesen.',
              detail: 'Der Auszug beantwortet oft schon, zu welchem Kunden die Mail gehört.',
            },
            {
              text: 'Reicht der Auszug nicht: auf den Betreff klicken.',
              detail:
                'Die ganze Mail öffnet sich mit Absender, Betreff, Eingangszeit und vollständigem Text. Der Kunde steht häufig erst in der Signatur oder im zitierten Verlauf einer weitergeleiteten Mail. Aus dieser Ansicht geht es mit „Kunde zuweisen“ oder „Ablehnen“ direkt weiter. Die Mail erscheint als reiner Text — Bilder, Logos und Formatierung werden nicht angezeigt, weil die Nachricht ungeprüft von außen kommt.',
            },
            {
              text: '„Kunde zuweisen“ — im Feld „Kunde“ Namen oder Kundennummer tippen, wählen, bestätigen.',
              detail:
                'Der Dialog zeigt die ganze Mail oberhalb der Kundensuche, damit Sie beim Suchen nachlesen können. Es entsteht ein Ticket in der Warteschlange des Postfachs, mit dem Originaltext als Beitrag. Eine Eingangsbestätigung wird nicht nachgeholt: Die Mail ist Tage alt, und eine Bestätigung dafür erklärt dem Absender vor allem, dass sie liegen geblieben ist.',
            },
            {
              text: 'Wahlweise „Absender als Ansprechpartner anlegen“ ankreuzen.',
              detail:
                'Dann wird die nächste Mail dieser Adresse von selbst zugeordnet. Der Haken steht aus: Wer einmal schreibt, ist noch kein Ansprechpartner.',
            },
            {
              text: '„Ablehnen“ für alles, was kein Kunde ist — Werbung, Irrläufer, Anfragen von außen.',
              detail:
                'Der Absender bekommt eine Absage: Wir konnten die Anfrage keinem aktiven Kunden zuordnen. Ein Grund wird ihm nicht genannt. Was Sie ins Feld „Interner Grund“ schreiben, steht im Archiv und im Protokoll — nicht in der Mail.',
            },
          ],
        },
        {
          kind: 'shot',
          file: 'anfrage-lesen',
          caption:
            'Die ganze Mail nach einem Klick auf den Betreff. Die Absenderadresse verrät den Kunden nicht — die Signatur schon.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'Offen', text: 'Wartet auf eine Entscheidung. Das ist die Arbeitsliste.' },
            {
              term: 'Zugewiesen',
              text: 'Wurde zum Vorgang. Die Ticketnummer steht in der Zeile.',
            },
            {
              term: 'Abgelehnt',
              text: 'Absage ist hinausgegangen. Bleibt stehen — die Liste ist auch ein Nachweis.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Bei älteren Anfragen fehlt der Text',
          text: 'Nachrichten, die vor der Umstellung eingelesen wurden, haben keinen gespeicherten Inhalt — dafür gab es keine Spalte. Die Zeile sagt das ausdrücklich; Absender und Betreff stehen weiterhin da, und die Mail selbst liegt noch im Postfach.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Was hier nicht erscheint',
          text: 'Abwesenheitsnotizen und alles, was eine Filterregel verwirft, tauchen in keiner der drei Listen auf. Ebensowenig, was über eine hinterlegte E-Mail-Domäne einem Kunden zugeordnet werden konnte — daraus wird gleich ein Ticket. Die Anfragen sind eine Arbeitsliste und kein zweites Postfach.',
        },
        { kind: 'route', to: '/app/anfragen', label: 'Zu den Anfragen' },
      ],
    },
    {
      id: 'filter',
      title: 'Filterregeln',
      blocks: [
        {
          kind: 'text',
          text: 'Eine Bedingung je Regel. Was sie trifft, wird vorbelegt oder verworfen. Verwerfen ist für das, was zuverlässig kein Ticket ist — Abwesenheitsnotizen, Newsletter, Zustellberichte.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Mailschleifen',
          text: 'Automatische Antworten erkennt das System am Auto-Submitted-Kopf und setzt ihn bei eigenen Antworten. Zwei Ticketsysteme, die einander schreiben, kommen so nicht in Fahrt.',
        },
      ],
    },
    {
      id: 'signaturen-vorlagen',
      title: 'Mailvorlagen, Anlässe und Signaturen',
      blocks: [
        {
          kind: 'text',
          text: 'Jede Mail, die das System verschickt, gehört zu einem Anlass: die Eingangsbestätigung, die Antwort eines Bearbeiters, die Einladung zur Umfrage, die Benachrichtigung über eine Zuweisung, der Termin, der geplante Bericht. Betreff und Text jeder dieser Mails stehen in einer Mailvorlage — nichts davon steht fest im Programm. Beides pflegen Sie im Reiter „Mailvorlagen“.',
        },
        {
          kind: 'shot',
          file: 'mailvorlagen',
          caption: 'Eine Vorlage im Editor, rechts die Variablen des gewählten Anlasses.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Mail-Anlässe',
              text: 'Die obere Liste. Je Anlass steht dort, an wen die Mail geht und welche Vorlage sie benutzt. Die Auswahl ändern Sie direkt in der Zeile; „Nicht versenden“ schaltet den Anlass ab.',
            },
            {
              term: 'Vorlagen',
              text: 'Die untere Liste. Eine Vorlage kann mehreren Anlässen dienen; daneben steht, welchen. Neue Vorlagen legen Sie mit „Neue Vorlage“ an.',
            },
            {
              term: 'Variablen',
              text: 'Platzhalter in doppelten geschweiften Klammern, etwa {{ticket.nummer}} oder {{kontakt.vorname}}. Beim Versand stehen dort die Werte des Vorgangs. Welche es gibt, hängt vom Anlass ab: Die Eingangsbestätigung kennt keinen Termin, die Absage kein Ticket.',
            },
            {
              term: 'Anrede und Signatur',
              text: '{{anrede}} und {{signatur}} kommen aus der Signatur der Warteschlange, sonst aus der Vorgabe des Mandanten. Wo sie in der Mail stehen, bestimmt die Vorlage.',
            },
          ],
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Unter „Vorlagen“ auf „Bearbeiten“ klicken oder „Neue Vorlage“ wählen.' },
            {
              text: 'Rechts unter „Variablen und Vorschau für“ den Anlass wählen, für den die Vorlage gedacht ist.',
              detail:
                'Die Liste darunter zeigt genau die Variablen, die dieser Anlass liefert, jede mit einer kurzen Erklärung.',
            },
            {
              text: 'Betreff und Text schreiben. Eine Variable setzen Sie per Klick ein.',
              detail:
                'Sie landet dort, wo Sie zuletzt geschrieben haben — im Betreff oder im Text.',
            },
            {
              text: '„Vorschau“ zeigt die Mail mit Beispielwerten.',
              detail:
                'Nennt die Vorschau Platzhalter, die der Anlass nicht kennt, blieben diese Stellen in der echten Mail leer.',
            },
            { text: 'Speichern und, bei einer neuen Vorlage, oben dem Anlass zuweisen.' },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Was das System verhindert',
          text: 'Ein Platzhalter, den es nicht gibt — etwa ein Tippfehler wie {{ticket.nummr}} —, wird beim Speichern abgewiesen. Ebenso eine Variable, die einer der zugewiesenen Anlässe nicht liefert. Eine Vorlage, die noch einem Anlass zugewiesen ist, lässt sich nicht löschen: Die Meldung nennt die Anlässe; erst umhängen, dann löschen.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Die Antwort an den Kunden lässt sich nicht abschalten',
          text: 'Die Vorlage „Antwort an den Kunden (Rahmen)“ umgibt jede Antwort eines Bearbeiters: Anrede, {{beitrag.text}}, Signatur, Portal-Link. Den Text schreibt der Bearbeiter am Ticket; die Vorlage bestimmt, was darum steht. „Nicht versenden“ gibt es hier nicht — sonst käme eine Antwort nie beim Kunden an. Die Ticketnummer setzt das System in den Betreff, auch wenn die Vorlage sie weglässt: Ohne sie fände die Antwort des Kunden nicht zum Vorgang zurück.',
        },
        {
          kind: 'text',
          text: 'Signaturen hängen an der Warteschlange; eine davon ist die Vorgabe des Mandanten. Sie werden im selben Editor geschrieben wie ein Beitrag am Ticket — ohne Überschriften und ohne Bild, denn sie stehen unter einer Mail und sind keine Seite.',
        },
        {
          kind: 'text',
          text: 'Eine geänderte Vorlage wirkt auf die nächste Mail. Was schon in der Versandwarteschlange steht, geht so hinaus, wie es beim Einreihen aussah.',
        },
        { kind: 'route', to: '/app/administration/mail', label: 'Zur E-Mail-Einrichtung' },
      ],
    },
    {
      id: 'versandwarteschlange',
      title: 'Die Versandwarteschlange',
      blocks: [
        {
          kind: 'text',
          text: 'Hier steht die Antwort auf „Der Kunde sagt, er hat nichts bekommen“. Die Warteschlange zeigt, was auf den Versand wartet, was gerade scheitert und was aufgegeben wurde — neueste zuerst, mit der Zahl der Versuche.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'wartet', text: 'Steht in der Schlange, der nächste Lauf nimmt es mit.' },
            { term: 'wird versendet', text: 'Gerade unterwegs.' },
            { term: 'versendet', text: 'Der Mailserver hat es angenommen.' },
            {
              term: 'fehlgeschlagen',
              text: 'Ein Versuch ist gescheitert; es wird erneut versucht.',
            },
            {
              term: 'aufgegeben',
              text: 'Alle Versuche sind aufgebraucht. Hier muss ein Mensch nachsehen.',
            },
          ],
        },
      ],
    },
  ],
};

const automatisierung: HandbookChapter = {
  id: 'automatisierung',
  title: 'Automatisierung',
  summary: 'Regeln, Zeitpläne, Wartungspläne — und wie man sie gefahrlos testet.',
  audience: 'admin',
  route: '/app/administration/automation',
  sections: [
    {
      id: 'regeln',
      title: 'Regeln',
      blocks: [
        {
          kind: 'shot',
          file: 'automatisierung',
          caption: 'Eine Regel mit Auslöser, Bedingungen und Aktionen.',
        },
        {
          kind: 'text',
          text: 'Eine Regel besteht aus drei Teilen: einem Auslöser, Bedingungen und Aktionen. Die Aktionen sind dieselbe feste Liste wie beim Makro.',
        },
        {
          kind: 'table',
          head: ['Auslöser', 'Wann er greift'],
          rows: [
            ['Ticket angelegt', 'Sobald ein Ticket entsteht — über jeden Kanal'],
            ['Ticket geändert', 'Bei jeder Änderung an den Feldern'],
            ['Beitrag hinzugefügt', 'Wenn ein Beitrag geschrieben wird'],
            ['Zeitgesteuert', 'Nach Zeitplan, unabhängig von einem Ereignis'],
          ],
        },
        {
          kind: 'text',
          text: 'Bedingungen vergleichen ein Feld mit einem Wert. Verknüpft werden sie entweder mit „alle treffen zu“ oder „eine trifft zu“. Das Wertefeld richtet sich nach dem gewählten Feld: Unter customer_id wählen Sie einen Kunden, unter owner_id einen Bearbeiter, unter queue_id oder queue.name eine Warteschlange, unter is_major_incident Ja oder Nein. Eine Kennung müssen Sie nirgends abschreiben — gespeichert wird sie trotzdem, und in der Liste der Bedingungen steht wieder der Name.',
        },
        {
          kind: 'table',
          head: ['Vergleich', 'Bedeutung'],
          rows: [
            ['ist gleich / ist ungleich', 'Genauer Wert'],
            ['enthält / enthält nicht', 'Teilzeichenfolge, für Betreff und Text'],
            ['beginnt mit / endet mit', 'Anfang oder Ende'],
            ['ist leer / ist nicht leer', 'Ohne Wertangabe'],
            [
              'ist eines von',
              'Mehrere Werte auf einmal — gewählt als Marken, bei Textfeldern durch Komma getrennt',
            ],
            ['ist größer als / ist kleiner als', 'Vergleicht Zahlen, nicht Text'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Stufen sind rückwärts sortiert',
          text: 'Priorität 1 ist die höchste. „priority.level ist kleiner als 3“ heißt also „wichtiger als Mittel“ — nicht umgekehrt.',
        },
      ],
    },
    {
      id: 'regel-bauen',
      title: 'Eine Regel bauen und testen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: '„Neue Regel“ klicken, Namen vergeben, Auslöser wählen.' },
            {
              text: 'Bedingungen hinzufügen.',
              detail:
                'Ohne Bedingung greift die Regel bei jedem Auslöser. Das ist für Eingangsbestätigungen gewollt, sonst selten.',
            },
            { text: 'Aktionen zusammenstellen.' },
            {
              text: '„Nach dieser Regel abbrechen“ setzen, wenn keine weitere Regel mehr laufen soll.',
              detail: 'Regeln laufen in Reihenfolge. Ohne Abbruch greifen alle, die passen.',
            },
            {
              text: '„Testen“ und ein echtes Ticket über Nummer oder Betreff wählen.',
              detail:
                'Die Regel wird geprüft und meldet, ob sie greifen würde und was sie ändern würde. Das Ticket bleibt unverändert. Nutzen Sie das immer, bevor Sie einschalten.',
            },
            { text: 'Erst dann „Einschalten“.' },
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Regeln lösen Regeln aus',
          text: 'Eine Regel, die ein Ticket ändert, kann eine Regel auf „Ticket geändert“ auslösen. Das System zählt die Durchläufe je Ticket und bricht mit einer Systemmeldung ab, bevor es endlos wird — aber die Meldung im Ticket sagt Ihnen dann, dass Sie einen Kreis gebaut haben.',
        },
      ],
    },
    {
      id: 'zeitplaene',
      title: 'Zeitpläne und Wartungspläne',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Zeitplan führt Aktionen nach einem Cron-Ausdruck aus — fünf Felder: Minute, Stunde, Tag, Monat, Wochentag, in der Zeitzone des Mandanten. „0 2 * * *“ heißt täglich um zwei Uhr nachts.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Ohne Auswahl nimmt der Auftrag jedes offene Ticket',
          text: 'Bis zu 500 je Lauf. Grenzen Sie ein, bevor Sie einschalten — und benutzen Sie „Jetzt laufen lassen“, um zu sehen, wie viele Tickets angefasst werden.',
        },
        {
          kind: 'text',
          text: 'Ein Wartungsplan legt aus einer Ticketvorlage wiederkehrende Tickets an — für einen bestimmten Kunden, mit Vorlauf in Tagen. Der Vorlauf ist der Punkt: Eine Wartung soll sich planen lassen und nicht am Tag des Termins auf dem Tisch liegen.',
        },
      ],
    },
    {
      id: 'genehmigungen',
      title: 'Genehmigungen',
      blocks: [
        {
          kind: 'text',
          text: 'Am Ticket lässt sich im Reiter „Freigaben“ eine Genehmigung anfordern: worüber entschieden wird, wer entscheidet und ob alle zustimmen müssen oder die Hälfte genügt. Wer gefragt ist, sieht das in seinem Eingang — auch das, was er in Vertretung entscheiden darf.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Bei einer Ablehnung zählt die Begründung',
          text: 'Sie ist das Einzige, womit der Antragsteller weiterarbeiten kann. Ein „abgelehnt“ ohne Begründung erzeugt eine zweite Anfrage.',
        },
      ],
    },
  ],
};

const ankuendigungen: HandbookChapter = {
  id: 'ankuendigungen',
  title: 'Ankündigungen',
  summary: 'Störungsbanner und Hinweise — in der Bearbeitung, im Kundenportal oder in beiden.',
  audience: 'agent',
  route: '/app/administration/announcements',
  sections: [
    {
      id: 'banner',
      title: 'Ankündigungen und Störungsbanner',
      blocks: [
        { kind: 'shot', file: 'ankuendigungen', caption: 'Die Ankündigungen mit ihrer Laufzeit.' },
        {
          kind: 'text',
          text: 'Ankündigungen stehen unter Einstellungen → Kanäle. Schreiben darf sie, wer das Recht dazu hat — mitgeliefert auch die Rolle „Bearbeitung“: Eine Störung trägt ein, wer sie am Telefon bemerkt. Eine Ankündigung erscheint als Banner ganz oben — in der Bearbeitung, im Portal oder in beidem. Der Text wird im Editor geschrieben und darf Auszeichnung tragen; Überschriften, Bilder und Tabellen bietet die Leiste hier nicht an, weil im Band zwei Zeilen Platz sind.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Sie lässt sich nicht wegklicken',
          text: 'Das ist Absicht. Eine Meldung, die sich schließen lässt, ist beim zweiten Kollegen schon geschlossen — und die Störung, um die es geht, dauert noch an. Wenn sie vorbei ist, beenden Sie die Ankündigung; bis dahin steht sie.',
        },
        {
          kind: 'steps',
          steps: [
            { text: 'Überschrift und Text schreiben.' },
            {
              text: 'Dringlichkeit wählen.',
              detail:
                'Eine Störung unterbricht, ein Hinweis wartet. Vorlesende Programme unterscheiden das.',
            },
            { text: 'Ankreuzen, wo sie erscheinen soll — Bearbeitung, Portal oder beides.' },
            {
              text: 'Laufzeit setzen oder offen lassen.',
              detail: 'Offen heißt bis auf Widerruf. „Jetzt beenden“ nimmt sie sofort weg.',
            },
          ],
        },
        { kind: 'route', to: '/app/administration/announcements', label: 'Zu den Ankündigungen' },
      ],
    },
  ],
};

const webhooks: HandbookChapter = {
  id: 'webhooks',
  title: 'Webhooks',
  summary: 'Ereignisse an fremde Systeme melden — einrichten, Zustellungen prüfen.',
  audience: 'admin',
  route: '/app/administration/webhooks',
  sections: [
    {
      id: 'einrichten',
      title: 'Einen Webhook einrichten',
      blocks: [
        { kind: 'shot', file: 'webhooks', caption: 'Die eingerichteten Webhooks.' },
        {
          kind: 'text',
          text: 'Webhooks stehen unter Einstellungen → Schnittstellen, neben den API-Token und den Fremddiensten. Ein Webhook meldet Ereignisse an ein fremdes System. Die Zieladresse muss https sein und darf nicht ins eigene Netz zeigen — der Server prüft das beim Speichern. Das Geheimnis hat mindestens 32 Zeichen, wird verschlüsselt abgelegt und nie wieder angezeigt; der Empfänger prüft damit, dass die Meldung wirklich von hier kommt.',
        },
        {
          kind: 'text',
          text: 'Der Reiter „Zustellungen“ zeigt je Meldung, ob sie zugestellt wurde, wie oft es versucht wurde und ob sie aufgegeben ist. Nach mehreren Fehlschlägen in Folge schaltet sich ein Webhook selbst still — sonst schriebe das System dauerhaft gegen eine Wand.',
        },
        { kind: 'route', to: '/app/administration/webhooks', label: 'Zu den Webhooks' },
      ],
    },
  ],
};

const zugaenge: HandbookChapter = {
  id: 'zugaenge',
  title: 'Benutzer verwalten',
  summary: 'Mitarbeiter anlegen, Rolle setzen, Kennwort und zweiten Faktor zurücksetzen.',
  audience: 'admin',
  route: '/app/administration/users',
  sections: [
    {
      id: 'anlegen',
      title: 'Einen Mitarbeiter anlegen',
      blocks: [
        { kind: 'shot', file: 'zugaenge', caption: 'Die Benutzer dieses Mandanten.' },
        {
          kind: 'steps',
          steps: [
            { text: '„Mitarbeiter anlegen“ klicken.' },
            {
              text: 'Name, E-Mail-Adresse und Rolle eintragen.',
              detail:
                'Zur Wahl stehen die mitgelieferten Rollen und die eigenen Ihres Mandanten — aber nur solche, die nicht mehr dürfen als Sie selbst. Was eine Rolle gewährt, steht unter „Rollen“.',
            },
            {
              text: 'Das erzeugte Erstkennwort notieren.',
              detail:
                'Es erscheint genau einmal und wird nirgends gespeichert. Geben Sie es auf einem anderen Weg weiter als dem, über den Sie die Adresse mitteilen — und lassen Sie es nach der ersten Anmeldung wechseln.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Portalzugänge entstehen nicht hier',
          text: 'Hier entstehen ausschließlich Zugänge für die Bearbeitung. Ihre Kunden brauchen für den Regelfall gar keinen: Sie öffnen ihren Vorgang unter /portal mit E-Mail-Adresse und Vorgangsnummer, und der Link in jeder ausgehenden Mail bringt beides mit. Einen dauerhaften Portalzugang mit eigener Anmeldung — für Ansprechpartner, die den Überblick über alle Vorgänge ihres Hauses brauchen — kann die Anwendung derzeit nicht anlegen.',
        },
      ],
    },
    {
      id: 'ruecksetzen',
      title: 'Kennwort und zweiten Faktor zurücksetzen',
      blocks: [
        {
          kind: 'text',
          text: 'Diese Seite ist bewusst schmal. Sie kann das eine, was sonst niemand kann: den zweiten Faktor zurücksetzen. Ohne diesen Weg wäre die Entscheidung gegen Wiederherstellungscodes eine Sackgasse.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Die Person zweifelsfrei identifizieren.',
              detail:
                'Nicht über die Mailadresse, von der die Bitte kam — die ist möglicherweise gerade das Problem. Rückruf auf einer bekannten Nummer, persönlich, über die Führungskraft.',
            },
            {
              text: '„Zweiten Faktor zurücksetzen“ klicken und bestätigen.',
              detail:
                'Die Person richtet ihn bei der nächsten Anmeldung neu ein und kommt bis dahin an kein Recht heran. Der Vorgang steht im Protokoll.',
            },
            {
              text: 'Bei einem vergessenen Kennwort stattdessen „Neues Kennwort vergeben“.',
              detail:
                'Das bisherige gilt danach nicht mehr, und alle Anmeldungen dieser Person enden.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Abschalten statt löschen',
          text: 'Wer das Haus verlässt, wird abgeschaltet. Seine Tickets, Beiträge und Zeiteinträge bleiben zugeordnet — das Protokoll muss auch in fünf Jahren noch sagen können, wer gehandelt hat.',
        },
        { kind: 'route', to: '/app/administration/users', label: 'Zu den Benutzern' },
      ],
    },
    {
      id: 'rolle-wechseln',
      title: 'Die Rolle eines Mitarbeiters wechseln',
      blocks: [
        {
          kind: 'text',
          text: 'In der Spalte „Rolle“ steht, welche Rolle jemand in diesem Mandanten trägt. Wer Benutzer verwalten darf, wählt dort eine andere; die neue Rolle gilt mit dem nächsten Klick der Person, ohne neue Anmeldung.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Zwei Sperren, beide mit Absicht',
          text: 'Die eigene Rolle lässt sich nicht wechseln — wer sich selbst herabstuft, sperrt sich womöglich aus. Und niemand ändert Rolle, Kennwort, zweiten Faktor oder Zustand eines Zugangs, der mehr darf als er selbst: Sonst wäre das Zurücksetzen des Administratorkennworts der Weg zu jedem Recht. Bei solchen Zugängen steht statt der Auswahl nur der Name der Rolle.',
        },
      ],
    },
  ],
};

const rollen: HandbookChapter = {
  id: 'rollen',
  title: 'Rollen und Rechte',
  summary: 'Sehen, was eine Rolle darf — und eigene Rollen anlegen, ändern und löschen.',
  audience: 'admin',
  route: '/app/administration/roles',
  sections: [
    {
      id: 'ansehen',
      title: 'Was eine Rolle darf',
      blocks: [
        {
          kind: 'shot',
          file: 'rollen',
          caption: 'Die Rolle „Bearbeitung“ mit ihren Rechten, nach Bereichen geordnet.',
        },
        {
          kind: 'text',
          text: 'Die Liste zeigt alle Rollen, die es in diesem Mandanten gibt: die mitgelieferten und die eigenen. „Vergeben an“ zählt, wie viele Personen sie hier tragen. Ein Klick auf eine Rolle zeigt darunter jedes Recht des Systems — gesetzt, wenn die Rolle es gewährt.',
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'Mitgeliefert',
              text: 'Kommt mit der Anwendung und gilt für alle Mandanten. Lässt sich ansehen und als Vorlage kopieren, aber nicht ändern — eine Änderung träfe jeden Mandanten.',
            },
            {
              term: 'Eigene',
              text: 'Gehört nur diesem Mandanten. Lässt sich ändern und löschen, solange niemand sie trägt.',
            },
            {
              term: 'Portal',
              text: 'Rollen für Kundenzugänge im Portal. Sie entstehen am Ansprechpartner im CRM und lassen sich unter „Benutzer“ nicht vergeben.',
            },
            {
              term: '2. Faktor',
              text: 'Wer ein so gekennzeichnetes Recht trägt, muss bei der Anmeldung einen zweiten Faktor einrichten. Es sind die Rechte, mit denen sich das System umbauen oder an personenbezogene Daten im Bestand kommen lässt.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wer die Seite sieht',
          text: 'Im Menü steht sie für alle, die Rollen verwalten dürfen — mitgeliefert ist das nur die Administration. Wer Benutzer anlegt, erreicht sie zusätzlich über den Verweis unter „Mitarbeiter anlegen“ und kann dort nachsehen, ohne etwas ändern zu können.',
        },
      ],
    },
    {
      id: 'anlegen',
      title: 'Eine eigene Rolle anlegen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Entweder „Neue Rolle“ — oder eine bestehende Rolle öffnen und „Als Vorlage kopieren“.',
              detail:
                'Die Vorlage ist der kürzere Weg zu „Bearbeitung, aber ohne CRM-Schreibrecht“: kopieren, ein Häkchen entfernen, fertig.',
            },
            {
              text: 'Einen Namen vergeben, den man in der Auswahl unter „Benutzer“ wiedererkennt.',
            },
            {
              text: 'Die Rechte ankreuzen und „Anlegen“ klicken.',
              detail:
                'Ausgegraut sind Rechte, die Sie selbst nicht tragen. Sie können sie nicht vergeben — sonst könnte sich jeder mit Rollenverwaltung eine Rolle mit allem bauen und sie sich selbst geben.',
            },
            { text: 'Unter „Benutzer“ die Rolle den Mitarbeitern zuweisen.' },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Eine Änderung wirkt sofort',
          text: 'Wer eine eigene Rolle ändert, ändert die Rechte aller, die sie tragen — mit dem nächsten Klick, ohne neue Anmeldung, und ebenso für deren API-Token. Jede Änderung steht mit den Rechten vorher und nachher im Protokoll.',
        },
      ],
    },
    {
      id: 'loeschen',
      title: 'Eine eigene Rolle löschen',
      blocks: [
        {
          kind: 'text',
          text: 'Löschen geht nur, solange niemand die Rolle trägt. Andernfalls ist der Knopf gesperrt, und darunter steht, an wie viele Personen sie vergeben ist. Weisen Sie diesen zuerst unter „Benutzer“ eine andere Rolle zu — sonst stünden sie ohne jedes Recht da, ohne dass es jemand bemerkt.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die eigene Rolle behält die Rollenverwaltung',
          text: 'Der Rolle, die Sie selbst tragen, lässt sich das Recht „Rollen verwalten“ nicht entziehen. Sie könnten sie danach nicht mehr ändern — auch nicht zurück.',
        },
        { kind: 'route', to: '/app/administration/roles', label: 'Zu den Rollen' },
      ],
    },
  ],
};

const token: HandbookChapter = {
  id: 'zugangstoken',
  title: 'API-Token für Maschinen',
  summary: 'Vollmachten für Skripte und Dienste — ausstellen, begrenzen, widerrufen.',
  audience: 'admin',
  route: '/app/administration/api-tokens',
  sections: [
    {
      id: 'grundsatz',
      title: 'Was ein Token ist',
      blocks: [
        { kind: 'shot', file: 'zugangstoken', caption: 'Die ausgestellten API-Token.' },
        {
          kind: 'text',
          text: 'Ein Token handelt im Namen dessen, der ihn ausstellt, und kann nie mehr als dieser. Wird Ihnen später eine Rolle entzogen, verliert der Token dieselben Rechte im selben Augenblick.',
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Was kein Token tragen darf',
          text: 'Ein Token ist von seiner Bauart her ein einziger Faktor: eine Zeichenfolge in einer Konfigurationsdatei. Rechte, die im Bedienweg einen zweiten Faktor verlangen, lassen sich deshalb nicht weitergeben — ebenso wenig die Vergabe von Zugängen. Der wichtigste Einzelfall: Ein Token kann keinen Token ausstellen. Was gestohlen wird, bleibt auf das begrenzt, was es beim Diebstahl konnte.',
        },
      ],
    },
    {
      id: 'ausstellen',
      title: 'Einen Token ausstellen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Namen vergeben.',
              detail:
                'Wofür der Token da ist. Er steht später in der Liste — „Hermes“ hilft beim Widerruf, „Token 3“ nicht.',
            },
            { text: 'Notiz: Welches System, wer betreut es.' },
            {
              text: 'Rechte ankreuzen — so wenige wie möglich.',
              detail: 'Ein Skript, das Tickets anlegt, braucht ticket.create und sonst nichts.',
            },
            {
              text: 'Gültigkeit setzen.',
              detail:
                'Höchstens ein Jahr. Ohne Angabe unbefristet — eine Frist ist der Anlass, den Bestand einmal anzusehen.',
            },
            {
              text: 'Den angezeigten Token sofort in den Kennwortspeicher übernehmen.',
              detail:
                'Er steht genau einmal da. Abgelegt ist nur, was er darf — nicht, wie er lautet.',
            },
          ],
        },
      ],
    },
    {
      id: 'widerrufen',
      title: 'Widerrufen',
      blocks: [
        {
          kind: 'text',
          text: 'Ein Widerruf wirkt mit der nächsten Anfrage und ist endgültig. Wer den Zugang wieder braucht, lässt einen neuen ausstellen. Die Spalte „Zuletzt benutzt“ hilft beim Aufräumen: Was seit Monaten niemand benutzt hat, braucht auch niemand.',
        },
        { kind: 'route', to: '/app/administration/api-tokens', label: 'Zu den API-Token' },
      ],
    },
  ],
};

const tokenmanagement: HandbookChapter = {
  id: 'tokenmanagement',
  title: 'Fremddienste',
  summary:
    'Zugangsdaten, mit denen sich ITSCRM bei fremden Diensten anmeldet — hinterlegen, prüfen, wechseln.',
  audience: 'admin',
  route: '/app/administration/tokens',
  sections: [
    {
      id: 'abgrenzung',
      title: 'Nicht zu verwechseln mit „API-Token“',
      blocks: [
        { kind: 'shot', file: 'tokenmanagement', caption: 'Die hinterlegten Zugänge.' },
        {
          kind: 'text',
          text: 'Beide stehen unter Einstellungen → Schnittstellen und sind das Gegenteil voneinander. Unter „API-Token“ gibt ITSCRM einer fremden Maschine eine Vollmacht. Hier hinterlegen Sie die Zugangsdaten, mit denen sich ITSCRM bei einem fremden Dienst anmeldet.',
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Zwei Richtungen, zwei Listen',
          text: 'Die eine Liste wird widerrufen, die andere gepflegt. In einer Maske zusammengelegt widerruft irgendwann jemand das Falsche.',
        },
      ],
    },
    {
      id: 'cove',
      title: 'Den Cove-Zugang eintragen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            { text: '„Zugang hinterlegen“ klicken.' },
            {
              text: 'Partnername eintragen — der Name, mit dem die Anmeldung erfolgt.',
              detail: 'Bei Cove der Anzeigename des Partners, nicht dessen Kennung.',
            },
            {
              text: 'Partnerkennung eintragen.',
              detail:
                'Pflichtangabe. Ohne sie lehnt Cove die Statistikabfrage mit Fehler 13501 ab — die Anmeldung gelänge trotzdem, und der Zugang sähe eingerichtet aus.',
            },
            { text: 'Kennung und Kennwort des API-Benutzers eintragen.' },
            {
              text: '„Verbindung prüfen“ klicken.',
              detail:
                'Die Prüfung geht bis zur Statistikabfrage. Eine Prüfung, die vorher aufhört, meldet „in Ordnung“ für einen Zugang, der nichts liefert.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Das Kennwort erscheint nie wieder',
          text: 'Es wird verschlüsselt abgelegt und nur an den Anbieter gereicht — dieselbe Regel wie beim Postfachkennwort. Beim Ändern bleibt das Feld leer, und leer heißt: unverändert. Wer es vergisst, trägt ein neues ein.',
        },
      ],
    },
    {
      id: 'fehler',
      title: 'Wenn die Prüfung fehlschlägt',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Fehler 13501',
              text: '„Operation failed because of security reasons.“ Häufigste Ursache: Die Partnerkennung fehlt oder der Zugang darf diesen Partner nicht sehen.',
            },
            {
              term: 'Anmeldung abgelehnt',
              text: 'Kennung oder Kennwort stimmen nicht — oder der Partnername passt nicht zum Benutzer.',
            },
            {
              term: 'Nicht erreichbar',
              text: 'Der Anbieter antwortet nicht. Die Adresse der Schnittstelle prüfen; sonst abwarten.',
            },
          ],
        },
        {
          kind: 'text',
          text: 'Der letzte Fehler bleibt in der Liste stehen, bis ein Abruf gelingt. Ohne diesen Vermerk fällt ein abgelaufenes Kennwort wochenlang niemandem auf — es kommen einfach keine Geräte mehr, und das sieht aus wie ein aufgeräumter Bestand.',
        },
        { kind: 'route', to: '/app/administration/tokens', label: 'Zu den Fremddiensten' },
      ],
    },
  ],
};

const sicherung: HandbookChapter = {
  id: 'sicherung',
  title: 'Sicherung',
  summary:
    'Welche Geräte gesichert werden, welche nicht — und wie man eine Sicherung erneut anstößt.',
  audience: 'agent',
  route: '/app/backup',
  sections: [
    {
      id: 'uebersicht',
      title: 'Die Übersicht lesen',
      blocks: [
        { kind: 'shot', file: 'sicherung', caption: 'Die Sicherungsübersicht mit ihren Zahlen.' },
        {
          kind: 'text',
          text: 'Gescheiterte Geräte stehen oben. Jede Zeile nennt den Zustand, die Fehleranzahl und die Tage seit der letzten erfolgreichen Sicherung — alle drei zusammen, weil keine der Angaben allein trägt.',
        },
        {
          kind: 'terms',
          items: [
            { term: 'Fehlgeschlagen', text: 'Die Sicherung ist nicht durchgelaufen.' },
            {
              term: 'Mit Fehlern abgeschlossen',
              text: 'Der Anbieter meldet „Completed“ und zählt trotzdem Fehler. Das ist eine eigene Kategorie und kein grünes „OK“ — ein Teil der Daten fehlt.',
            },
            { term: 'Läuft', text: 'Die Sicherung ist noch unterwegs. Kein Fehler.' },
            {
              term: 'Unbekannt',
              text: 'Kein Ergebnis vorhanden. Ebenfalls nicht grün: Ein Gerät, von dem niemand etwas weiß, ist im Zweifel schlechter dran als eines mit bekanntem Fehler.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: '„Abgeschlossen“ mit Fehlern ist ein Fehler',
          text: 'Am 07.09.2026 stand ein Gerät auf „Completed“, das seit dem 24.02. nicht mehr gesichert hatte. Deshalb steht die Fehleranzahl in jeder Zeile — auch die Null.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Wenn alle Geräte auf einmal „Unbekannt“ sind',
          text: 'Ein einzelnes Gerät ohne Ergebnis ist ein Gerät ohne Ergebnis. Steht dagegen die ganze Liste auf „Unbekannt“ und „Nie“, ohne Namen, dann fehlt nicht der Bestand, sondern die Antwort des Anbieters — der Abgleich hat die Geräte gefunden, aber ihre Werte nicht gelesen. Das ist kein Sicherungsproblem, sondern eines der Schnittstelle; melden statt nachsichern.',
        },
      ],
    },
    {
      id: 'tickets',
      title: 'Woher die Backup-Tickets kommen',
      blocks: [
        {
          kind: 'text',
          text: 'Die Benachrichtigungen des Anbieters laufen als E-Mail ein und werden gelesen, bevor daraus ein Ticket wird: Gerät, Kunde, Datenquelle, Fehleranzahl und der Zeitpunkt der Sitzung stehen danach als Felder da und nicht nur als Fließtext.',
        },
        {
          kind: 'example',
          title: 'Ein Ticket statt sechs',
          text: 'Die Meldung zu einem Gerät kommt täglich und beschreibt jedes Mal dieselbe gescheiterte Sitzung. Erkannt wird das an Gerät, Datenquelle und Sitzungszeitpunkt zusammen. Kommt sie erneut, bekommt das bestehende Ticket einen internen Vermerk „erneut eingegangen“ — kein zweites Ticket. Am 04.09.2026 entstanden auf dem alten Weg sechs Tickets für einen Fehler.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Das ist keine Stummschaltung',
          text: 'Eine andere Datenquelle, eine spätere Sitzung oder derselbe Fehler nach dem Abschluss des Tickets sind ein neuer Vorgang und bekommen ein eigenes Ticket.',
        },
      ],
    },
    {
      id: 'erneut',
      title: 'Eine Sicherung erneut starten',
      blocks: [
        {
          kind: 'text',
          text: 'Der Knopf steht an zwei Stellen: am Backup-Ticket selbst — dort, wo Sie ohnehin arbeiten — und in der Geräteakte der Sicherungsübersicht. Am Ticket erscheint er nur, wenn es eines ist; bei jedem anderen Vorgang steht dort nichts.',
        },
        {
          kind: 'steps',
          steps: [
            {
              text: 'Am Backup-Ticket den Kasten „Sicherung“ ansehen — oder in der Übersicht auf die Zeile des Geräts klicken.',
            },
            {
              text: '„Sicherung erneut starten“ klicken.',
              detail:
                'Verlangt das Recht „backup.manage“ — es wirkt beim Kunden und nicht in diesem System.',
            },
            {
              text: 'Den Stand darunter verfolgen.',
              detail:
                'Angenommen → Läuft → Abgeschlossen oder Fehlgeschlagen. Solange der Auftrag läuft, fragt ITSCRM beim Anbieter nach; danach nicht mehr.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'danger',
          title: 'Ein abgelehnter Auftrag verschwindet nicht',
          text: 'Lehnt der Anbieter ab — etwa mit Fehler 13501 —, bleibt der Auftrag mit dem Zustand „Fehlgeschlagen“ und der Meldung stehen. Ein „OK“ auf einen abgelehnten Auftrag gibt es nicht.',
        },
      ],
    },
    {
      id: 'abgleich',
      title: 'Abgleich und Aktualität',
      blocks: [
        {
          kind: 'text',
          text: '„Bestand abgleichen“ holt den Stand beim Anbieter. Was zuletzt abgeglichen wurde, steht über der Liste — ohne diese Angabe wäre jede Zahl auf der Seite ohne Alter. Einen Partner, den der Anbieter nicht herausgibt, meldet der Lauf ausdrücklich, statt ihn zu übergehen.',
        },
        {
          kind: 'text',
          text: '„Abgleich prüfen“ stellt beide Quellen gegenüber: Jedes Gerät, das der Anbieter als fehlerhaft meldet, muss ein Ereignis im Bestand haben — und umgekehrt. Kein Befund heißt bestanden; eine Toleranz gibt es nicht.',
        },
        { kind: 'route', to: '/app/backup', label: 'Zur Sicherung' },
      ],
    },
  ],
};

const systemstatus: HandbookChapter = {
  id: 'systemstatus',
  title: 'Systemstatus und Fassung',
  summary:
    'Welcher Stand läuft, ob der Server in Ordnung ist, ob die nächtliche Sicherung gelaufen ist — und was zuletzt ausgeliefert wurde.',
  audience: 'agent',
  route: '/app/status',
  sections: [
    {
      id: 'aufrufen',
      title: 'Die Seite aufrufen',
      blocks: [
        {
          kind: 'text',
          text: 'Unten in der Navigation, mittig unter dem Handbuch, steht die Build-Nummer — etwa „Build 140“. Sie ist die Zahl der Auslieferungen auf dem Hauptzweig und wächst mit jeder. Wer mit der Maus darauf zeigt, sieht Commit und Datum; ein Klick öffnet die Statusseite. Über die Befehlspalette ist sie unter „Systemstatus“ zu finden.',
        },
        {
          kind: 'text',
          text: 'Die Seite sehen alle Mitarbeiter — Administration, Bearbeitung und lesende Bearbeitung (Recht „system.read“). Portalzugänge sehen sie nicht. Sie enthält keine Daten eines Mandanten, sondern nur den Zustand des Servers.',
        },
        {
          kind: 'shot',
          file: 'systemstatus',
          caption: 'Die Statusseite: oben die Gesamtaussage, darunter die Abschnitte.',
        },
      ],
    },
    {
      id: 'lesen',
      title: 'Die Seite lesen',
      blocks: [
        {
          kind: 'text',
          text: 'Oben steht eine Aussage: „Alles in Ordnung“, „Es gibt Warnungen“ oder „Es gibt Störungen“. Sie ist der schlechteste Zustand aller Abschnitte. Ist sie nicht grün, nennt die Zeile daneben, welche Abschnitte es sind. Die Seite fragt jede Minute neu; „Jetzt prüfen“ fragt sofort.',
        },
        {
          kind: 'table',
          head: ['Abschnitt', 'Warnung ab', 'Störung ab'],
          rows: [
            ['Prozessorlast (Fünfminutenmittel je Kern)', '1,0', '2,0'],
            ['Arbeitsspeicher belegt', '85 %', '95 %'],
            ['Plattenplatz belegt', '80 %', '90 %'],
            ['Nächtliche Sicherung', 'kein Lauf gemeldet', 'gescheitert oder älter als 26 Stunden'],
            ['Geprüfter Rückweg', 'kein Lauf gemeldet', 'gescheitert oder älter als 35 Tage'],
            ['Datenbank', '—', 'nicht erreichbar oder Schemastand weicht vom Code ab'],
            ['Dienste', 'Timer abgeschaltet', 'Dienst gescheitert oder angehalten'],
            ['TLS-Zertifikat', 'weniger als 21 Tage gültig', 'weniger als 7 Tage oder ungültig'],
          ],
        },
        {
          kind: 'terms',
          items: [
            {
              term: 'unbekannt',
              text: 'Lässt sich auf diesem Rechner nicht messen — etwa ohne systemd auf einem Entwicklungsrechner. Kein Fehler des Servers; die Gesamtaussage bleibt davon unberührt.',
            },
            {
              term: 'Nächtliche Sicherung',
              text: 'Die Sicherung dieses Systems selbst — Datenbank und Anhänge, jede Nacht um 03:00. Nicht zu verwechseln mit der Sicherungsübersicht der Kundengeräte unter „Sicherung“.',
            },
            {
              term: 'Geprüfter Rückweg',
              text: 'Der monatliche Lauf, der den jüngsten Auszug in eine Wegwerf-Datenbank zurückspielt und prüft. Erst er belegt, dass die Sicherung im Ernstfall brauchbar ist.',
            },
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          title: 'Ein gelungener, aber alter Lauf ist rot',
          text: 'Steht die Sicherung auf „gelungen“ und trotzdem auf Störung, ist sie zu alt: Der Timer läuft nicht mehr. Das ist so ernst wie ein Fehlschlag — die Daten seit dem letzten Lauf sind ungesichert.',
        },
        {
          kind: 'note',
          tone: 'info',
          title: '„Kein Lauf gemeldet“',
          text: 'Die Sicherungsskripte legen ihr Ergebnis im Statusverzeichnis des Servers ab. Fehlt die Meldung, läuft entweder der Timer nicht, oder das Skript darf das Verzeichnis nicht beschreiben. Beides gehört an die Administration des Servers.',
        },
      ],
    },
    {
      id: 'aenderungen',
      title: 'Der Änderungsverlauf',
      blocks: [
        {
          kind: 'text',
          text: 'Unten stehen die zuletzt ausgelieferten Änderungen, nach Tag geordnet, jede mit ihrer Build-Nummer. Darüber eine Zeile, wie viel in den letzten vierzehn Tagen neu kam und wie viel behoben wurde. Zunächst erscheint nur, was Anwender betrifft: „Neu“, „Behoben“ und „Schneller“. „Alle Änderungen zeigen“ nimmt Tests, Dokumentation und Pflege hinzu.',
        },
        {
          kind: 'text',
          text: 'Der Verlauf entsteht aus den Commit-Beschreibungen und wird nicht von Hand geführt. Er ist deshalb nie hinter dem Stand zurück — aber er ist so knapp formuliert wie diese.',
        },
      ],
    },
  ],
};

export const betriebChapters: HandbookChapter[] = [
  mail,
  ankuendigungen,
  automatisierung,
  zugaenge,
  rollen,
  token,
  webhooks,
  tokenmanagement,
  sicherung,
  systemstatus,
];
